/**
 * Phase 8 T-18 §26.D — KPI card with optional 👍/👎 feedback affordance.
 *
 * Extracted from DashboardKpiRow so the feedback UX can be unit-tested
 * independently. The parent passes `enableFeedback` (default false).
 *
 * Hover behaviour:
 *   1. 👍 / 👎 buttons appear in the top-right corner.
 *   2. Clicking either opens an inline 3-line textarea + [Submit].
 *   3. On submit: card fades once + global toast.
 *
 * CLS guard: the textarea expands inside an absolute-positioned overlay that
 * does NOT affect block flow — this keeps layout shift < 0.02 per card.
 */

import { useCallback, useId, useRef, useState } from 'react';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';
import type { LucideIcon } from 'lucide-react';

export type KpiCardTone = 'good' | 'average' | 'low';

export interface KpiCardProps {
  /** Unique key (used as aria label suffix). */
  kpiKey: string;
  label: string;
  icon: LucideIcon;
  value: string | number;
  hint?: string;
  badge?: { label: string; tone: KpiCardTone } | null;
  loading?: boolean;
  error?: boolean;
  unavailable?: boolean;
  /** When true, a 👍/👎 pair appears on hover — §26.D. Default: false. */
  enableFeedback?: boolean;
}

function ToneBadge({ tone, children }: { tone: KpiCardTone; children: React.ReactNode }) {
  const cls =
    tone === 'good'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : tone === 'average'
        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
        : 'bg-rose-500/15 text-rose-700 dark:text-rose-300';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {children}
    </span>
  );
}

type FeedbackState = 'idle' | 'open' | 'submitted';

export function KpiCard({
  kpiKey,
  label,
  icon: Icon,
  value,
  hint,
  badge,
  loading,
  error,
  unavailable,
  enableFeedback = false,
}: KpiCardProps) {
  const { showToast } = useGlobalToast();
  const [hovered, setHovered] = useState(false);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('idle');
  const [thumbChoice, setThumbChoice] = useState<'up' | 'down' | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [fading, setFading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaId = useId();

  const openFeedback = useCallback(
    (thumb: 'up' | 'down') => {
      setThumbChoice(thumb);
      setFeedbackState('open');
      setTimeout(() => textareaRef.current?.focus(), 40);
    },
    [],
  );

  const closeFeedback = useCallback(() => {
    setFeedbackState('idle');
    setThumbChoice(null);
    setFeedbackText('');
  }, []);

  const submitFeedback = useCallback(() => {
    console.info('[KpiCard] feedback submitted:', {
      kpi: kpiKey,
      thumb: thumbChoice,
      comment: feedbackText.trim(),
    });

    setFeedbackState('submitted');
    setFeedbackText('');

    // Card fade once, then reset
    setFading(true);
    setTimeout(() => setFading(false), 600);

    showToast({
      variant: 'success',
      title: 'Thanks — your feedback helps us tune the model.',
    });

    setTimeout(closeFeedback, 300);
  }, [kpiKey, thumbChoice, feedbackText, showToast, closeFeedback]);

  const showThumbs =
    enableFeedback && hovered && feedbackState !== 'submitted';

  return (
    <div
      className={`relative rounded-[10px] border vscode-border vscode-bg-secondary p-4 transition-opacity duration-500 ${
        fading ? 'opacity-30' : 'opacity-100'
      }`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        if (feedbackState === 'open') closeFeedback();
      }}
    >
      {/* Row: icon + label + badge */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 vscode-text-tertiary" aria-hidden />
          <p className="truncate text-[11px] uppercase tracking-wider vscode-text-tertiary">
            {label}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {badge ? <ToneBadge tone={badge.tone}>{badge.label}</ToneBadge> : null}

          {/* 👍 / 👎 — only visible on hover */}
          {showThumbs ? (
            <div
              className="flex items-center gap-0.5"
              role="group"
              aria-label={`Rate ${label}`}
            >
              <button
                type="button"
                onClick={() => openFeedback('up')}
                className={`rounded p-0.5 text-[13px] leading-none transition-transform hover:scale-110 ${
                  thumbChoice === 'up' ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
                aria-label={`Thumbs up for ${label}`}
                aria-pressed={thumbChoice === 'up'}
              >
                👍
              </button>
              <button
                type="button"
                onClick={() => openFeedback('down')}
                className={`rounded p-0.5 text-[13px] leading-none transition-transform hover:scale-110 ${
                  thumbChoice === 'down' ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
                aria-label={`Thumbs down for ${label}`}
                aria-pressed={thumbChoice === 'down'}
              >
                👎
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Value */}
      <p
        className={`mt-2 text-2xl font-semibold tabular-nums ${
          unavailable || error
            ? 'text-base font-medium vscode-text-tertiary'
            : 'vscode-text-primary'
        }`}
      >
        {error ? (
          '—'
        ) : loading ? (
          <span className="inline-block h-6 w-12 animate-pulse rounded vscode-bg-tertiary" />
        ) : (
          value
        )}
      </p>

      <p className="mt-1 text-[11px] vscode-text-tertiary">
        {error ? 'Data unavailable' : hint}
      </p>

      {/* Inline feedback form — absolutely positioned to avoid layout shift (CLS < 0.02). */}
      {feedbackState === 'open' ? (
        <div
          className="absolute inset-x-0 bottom-0 z-10 rounded-b-[10px] border-t bg-[var(--vscode-bg-secondary,#fff)] p-3 shadow-lg vscode-border vscode-bg-secondary dark:bg-[var(--vscode-bg-secondary)]"
          role="group"
          aria-label={`Submit ${thumbChoice === 'up' ? 'positive' : 'negative'} feedback for ${label}`}
        >
          <label
            htmlFor={textareaId}
            className="mb-1.5 block text-[11px] vscode-text-secondary"
          >
            {thumbChoice === 'up' ? 'What looks good?' : 'What could be better?'}{' '}
            <span className="vscode-text-tertiary">(optional)</span>
          </label>
          <textarea
            id={textareaId}
            ref={textareaRef}
            rows={3}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Add a comment…"
            className="w-full resize-none rounded-[6px] border px-2 py-1.5 text-[12px] vscode-border vscode-bg-secondary vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[#10B981]"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeFeedback}
              className="rounded-[6px] px-3 py-1 text-[12px] vscode-text-tertiary transition-colors hover:vscode-text-primary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitFeedback}
              className="rounded-[6px] bg-[#10B981] px-3 py-1 text-[12px] font-medium text-white transition-opacity hover:opacity-90"
            >
              Submit
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
